import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import LightSelect from "../../CompanyView/LightSelect";
import InfoTip from "./InfoTip";
import { UseCompanySubWorkspaces, UseCompanyWorkspaces } from "../../../hooks/company";
import {
  DEFAULT_COLS,
  MAX_COLS,
  autoMetricsFor,
  colsOf,
  metricsForWorkspace,
  pickableMetricsFor,
  pickedWorkspaceIds,
  resolveRows,
  subsExplicit,
} from "../../../components/DeptMetrics/data";
import "./customize.css";

/**
 * Setup for All Department Metrics.
 *
 * Two questions: which workspaces get a row, and which of each one's metrics
 * land in its cells. The second has a real answer already — the workspace's
 * own Live set, the metrics that team sees on its own dashboard — so every row
 * starts on Automatic and only the rows an admin actually wants to differ need
 * touching. That matters: a company with eight workspaces would otherwise be
 * eight pickers to fill before the card showed anything at all.
 */

const IcChevron = (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

const IcCheck = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const DeptMetricsSetup = ({ draft, onChange }) => {
  const workspaces = UseCompanyWorkspaces(true) || [];
  const subWsByParent = UseCompanySubWorkspaces();
  const companyKpis = useSelector((s) => s.kpiReducer.companyKpis);

  const set = (patch) => onChange({ ...draft, ...patch });
  const picked = pickedWorkspaceIds(draft);
  const explicit = subsExplicit(draft);
  const clearedOut = explicit && picked.length === 0;
  const cols = colsOf(draft);

  let treeHint = "Every workspace is ticked by default, sub-workspaces included, and the list stays current as teams are added. Untick any one to drop it.";
  if (clearedOut) {
    treeHint = "Nothing ticked — the card will stay blank until you pick at least one workspace above.";
  } else if (picked.length > 0) {
    treeHint = "Only the ticked workspaces show, exactly as ticked — untick one, tick another, or pick just some of a group's sub-workspaces.";
  }

  /* The rows the card will actually draw, resolved by the very function the
     card uses — so this panel can never list a different set than the board
     shows, which is the whole failure mode of a Setup that rebuilds its own
     preview of the rules. */
  const rows = resolveRows(draft, workspaces, subWsByParent);
  const includedIds = new Set(rows.map((r) => r.workspaceId));

  /* Two independent expand states on purpose: the Departments tree below and
     the "Metrics per department" accordion further down both key off a
     workspace id, and sharing one object would make expanding a workspace's
     sub-workspaces here also pop its metrics picker open below. */
  const [openWs, setOpenWs] = useState({});
  const toggleOpenWs = (id) => setOpenWs((o) => ({ ...o, [id]: !o[id] }));
  const [open, setOpen] = useState({});
  const toggleOpen = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));

  const topLevel = workspaces.filter((w) => !w.parentId);

  /* Ticking or unticking ANY single workspace — top-level or sub, whether or
     not its parent is also ticked — freezes exactly what the card is
     currently showing (`rows`, the same resolver the board uses) and flips
     just that one id. That is what lets a company keep only some of a
     group's sub-workspaces, or pick a sub-workspace with no parent row at
     all — the one thing the old all-or-nothing "include sub-workspaces"
     switch could never do. Unticking a parent also drops its sub-workspaces
     with it — a hidden row can't have visible children under it. */
  const toggleInclude = (id) => {
    const wsId = String(id);
    const already = includedIds.has(wsId);
    const frozen = rows.map((r) => r.workspaceId);
    let next;
    if (already) {
      const kidIds = new Set((subWsByParent?.[wsId] || []).map((k) => String(k._id)));
      next = frozen.filter((x) => x !== wsId && !kidIds.has(x));
    } else {
      next = [...frozen, wsId];
    }
    set({ workspaceIds: next, subsExplicit: true });
  };

  /* Clears every pick and drops back to the dynamic default: every workspace
     the company has, sub-workspaces included, staying current as teams are
     added or removed — the same "Back to automatic" idea the metrics picker
     below already uses. */
  const resetToAutomatic = () => set({ workspaceIds: [], subsExplicit: false });

  const setRowMetrics = (wsId, ids) => set({
    metricsByWs: { ...(draft?.metricsByWs || {}), [String(wsId)]: ids },
  });

  const toggleMetric = (wsId, kpiId, catalog) => {
    const current = metricsForWorkspace(draft, wsId);
    /* First hand-pick starts from what the row is showing RIGHT NOW (its Live
       set), not from empty — clicking one metric to add it should not silently
       drop the four already on screen. */
    const base = current.length ? current : autoMetricsFor(catalog, cols).map((k) => String(k._id));
    const next = base.includes(kpiId) ? base.filter((x) => x !== kpiId) : [...base, kpiId];
    setRowMetrics(wsId, next);
  };

  return (
    <>
      <div className="gmbw-fld">
        <label>
          Departments
          <InfoTip text="One row per workspace. Each row shows that workspace's own metrics — departments rarely track the same things, so the columns are positions, not shared headings. Expand a workspace with the chevron to reach its own sub-workspaces and tick or untick them one at a time." />
        </label>
        <div className="gmbw-dm-tree">
          {topLevel.map((w) => {
            const id = String(w._id);
            const on = includedIds.has(id);
            const kids = subWsByParent?.[id] || [];
            const isOpen = !!openWs[id];
            return (
              <div key={id}>
                <div className="gmbw-dm-treehead">
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={on}
                    className={`gmbw-wsopt${on ? " on" : ""}`}
                    onClick={() => toggleInclude(id)}
                  >
                    <span className="gmbw-wsbox">{on && IcCheck}</span>
                    <span className="gmbw-wsnm">{w.name}</span>
                  </button>
                  {kids.length > 0 && (
                    <button
                      type="button"
                      className={`gmbw-dm-treechev${isOpen ? " open" : ""}`}
                      aria-expanded={isOpen}
                      aria-label={`${isOpen ? "Hide" : "Show"} ${w.name}'s sub-workspaces`}
                      onClick={() => toggleOpenWs(id)}
                    >
                      {IcChevron}
                    </button>
                  )}
                </div>
                {kids.length > 0 && isOpen && (
                  <div className="gmbw-dm-treekids">
                    {kids.map((k) => {
                      const kid = String(k._id);
                      const kon = includedIds.has(kid);
                      return (
                        <button
                          key={kid}
                          type="button"
                          role="checkbox"
                          aria-checked={kon}
                          className={`gmbw-wsopt${kon ? " on" : ""}`}
                          onClick={() => toggleInclude(kid)}
                        >
                          <span className="gmbw-wsbox">{kon && IcCheck}</span>
                          <span className="gmbw-wsnm">{k.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <p className="gmbw-hint">{treeHint}</p>
        {(picked.length > 0 || clearedOut) && (
          <button type="button" className="gmbw-dm-reset" onClick={resetToAutomatic}>
            Back to automatic
          </button>
        )}
      </div>

      <div className="gmbw-fld">
        <label>
          Metrics per department
          <InfoTip text="Automatic shows that workspace's Live metrics — the ones already on its own dashboard, in its own order. Open a row to pick different ones just for it." />
        </label>
        {rows.length === 0 ? (
          <p className="gmbw-hint">Pick a workspace above, and it will appear here.</p>
        ) : rows.map((row) => {
          const catalog = pickableMetricsFor(companyKpis?.[row.workspaceId] || []);
          const chosen = metricsForWorkspace(draft, row.workspaceId);
          const auto = chosen.length === 0;
          const showing = auto
            ? autoMetricsFor(catalog, cols).map((k) => String(k._id))
            : chosen;
          const isOpen = !!open[row.workspaceId];
          return (
            <div className={`gmbw-mrow${row.depth ? " gmbw-dm-sub" : ""}`} key={row.key}>
              <button
                type="button"
                className="gmbw-dm-rowhead"
                aria-expanded={isOpen}
                onClick={() => toggleOpen(row.workspaceId)}
              >
                <span className={`gmbw-dm-chev${isOpen ? " open" : ""}`}>{IcChevron}</span>
                <span className="gmbw-dm-rowname">{row.name}</span>
                <span className="gmbw-dm-rowtag">
                  {catalog.length === 0
                    ? "no metrics"
                    : `${auto ? "Automatic" : "Hand-picked"} · ${Math.min(showing.length, cols)}`}
                </span>
              </button>
              {isOpen && (
                <div className="gmbw-dm-metrics">
                  {catalog.length === 0 ? (
                    <p className="gmbw-hint" style={{ margin: 0 }}>
                      This workspace tracks no metrics of its own. Its row will say so on the card — add metrics
                      to it, or leave it out above.
                    </p>
                  ) : (
                    <>
                      <div className="gmbw-wspick gmbw-dm-cbscroll">
                        {catalog.map((k) => {
                          const id = String(k._id);
                          const on = showing.includes(id);
                          const over = on && showing.indexOf(id) >= cols;
                          return (
                            <button
                              type="button"
                              key={id}
                              role="checkbox"
                              aria-checked={on}
                              className={`gmbw-wsopt${on ? " on" : ""}${over ? " over" : ""}`}
                              onClick={() => toggleMetric(row.workspaceId, id, catalog)}
                              title={over ? `Past the ${cols}-metric limit — it won't fit on the card` : undefined}
                            >
                              <span className="gmbw-wsbox">
                                {on && (
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 6 9 17l-5-5" />
                                  </svg>
                                )}
                              </span>
                              <span className="gmbw-wsnm">{k.title || k.name || "Metric"}</span>
                            </button>
                          );
                        })}
                      </div>
                      {!auto && (
                        <button
                          type="button"
                          className="gmbw-dm-reset"
                          onClick={() => setRowMetrics(row.workspaceId, [])}
                        >
                          Back to automatic
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
        <p className="gmbw-hint">
          A hand-picked row keeps the order you tick them in. Anything past the metric limit below is greyed —
          it stays saved, it just doesn&apos;t fit on the card.
        </p>
      </div>

      <div className="gmbw-fld">
        <label>
          Metrics per row
          <InfoTip text="How many cells each row gets. Rows with fewer metrics leave the remaining cells empty so every row still lines up on the same columns." />
        </label>
        <LightSelect
          width="100%"
          height={36}
          fontSize={12.5}
          radius={8}
          menuWidth={300}
          value={String(cols)}
          options={Array.from({ length: MAX_COLS }, (_, i) => ({
            value: String(i + 1),
            label: `${i + 1} metric${i === 0 ? "" : "s"}${i + 1 === DEFAULT_COLS ? " (default)" : ""}`,
          }))}
          onChange={(o) => set({ maxCols: Number(o.value) })}
        />
      </div>
    </>
  );
};

DeptMetricsSetup.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  draft: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default DeptMetricsSetup;

/* Appearance → how each metric tile is drawn. Shared by both editors (Home
   customize drawer and the Analytics component editor) so the two can't offer
   different wording. `tileStyle` stores only the non-default ("bar"); see the
   sanitizers in layoutStore.js / workspaceLayout.js. */
const TILE_STYLES = [
  { key: "climb", title: "Mountain climb", note: "Each tile draws the Yeti's climb behind the figure." },
  { key: "bar", title: "Progress bar", note: "Just a bar under the figure — green while beating the Yeti, red while behind." },
];

export const DeptTileStyle = ({ draft, onChange }) => {
  const current = draft?.tileStyle === "bar" ? "bar" : "climb";
  return (
    <div className="gmbw-fld">
      <label>Tile style</label>
      <div className="gmbw-showlist">
        {TILE_STYLES.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`gmbw-show${current === t.key ? " on" : ""}`}
            aria-pressed={current === t.key}
            onClick={() => onChange({ ...draft, tileStyle: t.key })}
          >
            <span className="gmbw-show-rad" />
            <span className="gmbw-show-t">
              <b>{t.title}</b>
              <span>{t.note}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};
DeptTileStyle.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  draft: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

/** True once the card has enough to draw something real. */
export const deptMetricsConfigured = () => true;

/* How a cell decides its colour, in the order data.js tries them. Worth
   spelling out here because the second rule is the surprising one: a metric
   the Yeti scores no points on this period is judged on the amount instead,
   and without that a whole column of anchor metrics would read green for
   nothing. */
const RULES = [
  { out: "Points", terms: ["Your points", "≥", "Yeti's points"] },
  { out: "Amount", terms: ["Your amount", "vs", "Yeti's pace"] },
  { out: "No pace", terms: ["no target", "or", "points rule"] },
];

const IcSigma = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 5H7l6 7-6 7h11" />
  </svg>
);

export const DeptMetricsFormulaCard = () => (
  <div className="gmbw-formula">
    <div className="gmbw-formula-h">
      <span className="gmbw-formula-ic">{IcSigma}</span>
      How a cell decides green or red
    </div>
    <dl className="gmbw-formula-list">
      {RULES.map((f) => (
        <div className="gmbw-formula-row" key={f.out}>
          <dt>{f.out}</dt>
          <dd>
            {f.terms.map((t, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <span key={i} className={/^[≥÷×−+]$/.test(t) ? "op" : "term"}>{t}</span>
            ))}
          </dd>
        </div>
      ))}
    </dl>
    <p className="gmbw-formula-note">
      <b>Points</b> first — the same rule the Beat the Yeti card uses, so the two never disagree.
      <b> Amount</b> only when the Yeti scores no points on that metric for the period, which is how
      anchor and whole-month metrics work outside a full month; without it they would read green for
      everyone. <b>No pace</b> greys the cell: a metric with no target has no Yeti to beat, and calling
      that a win would be a lie.
    </p>
  </div>
);
